"use client";

import Image from "next/image";
import { useState } from "react";
import { MapPinIcon, ArrowRightIcon, ChevronDownIcon, UsersIcon, TruckIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function VertexLanding() {
  // Toggle open panel
  const [openId, setOpenId] = useState<string | null>(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<string>("");
  const [bookingStatus, setBookingStatus] = useState<string | null>(null);
  /* 1. Service data */
  const services = [
    {
      id: "basic",
      title: "Basic Screening",
      blurb: "Quick insights, lasting peace of mind.",
      body: `Our Basic Screening package delivers the essential health metrics everyone should track—vital signs, complete blood count, blood‑sugar and lipid panels, plus a urinalysis—all performed with hospital‑grade equipment and a focus on speed and comfort. Results are turned around within 24 hours and published to your secure online portal, letting you catch early warning signs and make lifestyle tweaks before small issues become serious.`,
      image: "/images/basic-screening.png",
      alt: "Nurse chatting with patient while holding clipboard"
    },
    {
      id: "preemployment",
      title: "Pre‑Employment Check",
      blurb: "Hire with confidence, comply without compromise.",
      body: `This screening is designed for HR teams that need fast, credible fitness‑for‑work decisions. Beyond a thorough physical exam, we offer drug & alcohol testing, vision and auditory checks, vaccination status verification, and bespoke add‑ons for safety‑critical roles. You receive a concise medical fitness certificate that satisfies local regulations and minimizes onboarding delays.`,
      image: "/images/pre-employment.png",
      alt: "Doctor shaking hands with young professional"
    },
    {
      id: "staffscreening",
      title: "Diagnostic Staff Screening",
      blurb: "Protect your team. Safeguard your patients.",
      body: `Healthcare professionals face unique occupational risks, so this package targets infectious‑disease exposure and procedural fitness. We screen for Hep B, Hep C, HIV, and TB; confirm immunization titers; and assess manual‑handling and sharps safety competence. Optional periodic mental‑health check‑ins help combat burnout. Reports are aggregated for compliance dashboards yet remain privacy‑centric.`,
      image: "/images/staff-screening.png",
      alt: "Healthcare workers queued for temperature check"
    },
    {
      id: "comprehensive",
      title: "Comprehensive Medical Check",
      blurb: "Your 360° health audit—one appointment, one actionable report.",
      body: `Ideal for executives and anyone seeking a full‑scope health picture, this half‑day program layers advanced diagnostics onto our Basic Screening: cardiac stress ECG, abdominal & thyroid ultrasound, cancer‑marker panels, full metabolic work‑up, and lifestyle counseling. You leave with a physician‑led debrief, a prioritized action plan, and a beautifully structured report.`,
      image: "/images/comprehensive-check.png",
      alt: "Doctor drawing blood from smiling patient"
    }
  ];
  const toggle = (id: string) => setOpenId(prev => (prev === id ? null : id));
  // Use OpenStreetMap coordinates for Lekki Phase 1, Lagos
  const mapCoords = {
    lat: 6.433,
    lng: 3.452
  };
  
  // OpenStreetMap URL (no API key needed)
  const mapUrl = `https://www.openstreetmap.org/?mlat=${mapCoords.lat}&mlon=${mapCoords.lng}&zoom=15&layers=M`;
  
  // Static map image
  const mapSrc = "/images/map-preview.png";

  /* ——————————————————————————————————
     2.  Mark-up
  —————————————————————————————————— */
  return (
    <>
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="absolute inset-0">
          <Image
            src="/hero-bg.jpg"
            alt=""
            fill
            priority
            sizes="100vw"
            className="object-cover object-center opacity-40"
          />
        </div>

        <div className="relative mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-[420px] flex-col items-center justify-center py-16 sm:min-h-[480px] lg:min-h-[580px]">
            <Image 
              src="/logo.png" 
              alt="Vertex Diagnostic Center logo" 
              width={80}
              height={80}
              className="mb-6 w-20 sm:w-24 lg:w-28"
              priority 
            />
            
            <div className="mb-4 flex items-center rounded-full bg-white/10 px-3 py-1 text-sm text-white backdrop-blur-sm">
              <MapPinIcon className="mr-1.5 h-4 w-4 text-teal-400" />
              <span className="font-medium">Lekki Phase 1, Lagos</span>
            </div>

            <h1 className="mb-8 text-center text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl lg:leading-tight">
              Medical screenings for<br className="hidden lg:inline" /> individuals, employees & families.
            </h1>

            <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-center md:gap-4">
              <a 
                href="tel:08148390839"
                className="w-full rounded-lg bg-teal-600 px-6 py-3 text-center font-semibold text-white shadow-sm transition hover:bg-teal-700 sm:w-auto"
              >
                Call 0814 839 0839
              </a>
              <button
                type="button"
                onClick={() => setShowScheduler(true)}
                className="w-full rounded-lg bg-white px-6 py-3 text-center font-semibold text-gray-900 shadow-sm transition hover:bg-gray-50 sm:w-auto"
              >
                Book Online
              </button>
              <button 
                type="button" 
                onClick={() => setShowScheduler(true)}
                className="w-full rounded-lg bg-white/10 px-6 py-3 text-center font-semibold text-white shadow-sm backdrop-blur-sm transition hover:bg-white/20 sm:w-auto"
              >
                Email
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Accordion */}
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
        <div className="space-y-3 sm:space-y-4">
          {services.map(s => (
            <div key={s.id} className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition hover:border-gray-300">
              <button
                onClick={() => toggle(s.id)}
                className="flex w-full items-center justify-between p-4 text-left transition sm:p-6"
              >
                <div>
                  <h3 className="text-base font-semibold text-gray-900 sm:text-lg lg:text-xl">{s.title}</h3>
                  <p className="mt-1 text-sm text-gray-500 sm:text-base">{s.blurb}</p>
                </div>
                <ChevronDownIcon 
                  className={`h-5 w-5 flex-none text-gray-400 transition-transform duration-200 sm:h-6 sm:w-6 ${
                    openId === s.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              {openId === s.id && (
                <div className="border-t border-gray-200 bg-gray-50/50">
                  <div className="grid gap-6 p-4 sm:grid-cols-2 sm:p-6">
                    <div className="aspect-h-9 aspect-w-16 relative overflow-hidden rounded-lg bg-gray-100 shadow-inner sm:aspect-h-3 sm:aspect-w-4">
                      <Image 
                        src={s.image} 
                        alt={s.alt} 
                        fill
                        className="object-cover"
                        sizes="(min-width: 640px) 50vw, 100vw"
                      />
                    </div>
                    <div className="prose prose-sm prose-gray max-w-none sm:prose-base">
                      <p>{s.body}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Getting Here */}
      <section id="directions" className="bg-gradient-to-b from-white to-gray-50 py-8 sm:py-12" itemScope itemType="https://schema.org/HowTo">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-6 flex items-center justify-center text-xl font-semibold text-gray-900 sm:text-2xl">
            <MapPinIcon className="mr-2 h-6 w-6 text-teal-600 sm:h-8 sm:w-8" />
            Getting Here
          </h2>
          
          <div className="grid gap-4 sm:grid-cols-2">
            <details className="group overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200 transition-all hover:ring-gray-300">
              <summary className="flex cursor-pointer items-center justify-between bg-gray-50 p-4 text-sm font-medium text-gray-900 hover:bg-gray-100">
                <div className="flex items-center gap-2">
                  <UsersIcon className="h-4 w-4 text-teal-600" />
                  <span>By Public Transport</span>
                </div>
                <ChevronDownIcon className="h-5 w-5 text-gray-500 transition duration-200 group-open:rotate-180" />
              </summary>
              
              <div className="divide-y divide-gray-100">
                <div className="p-4">
                  <ol className="space-y-4">
                    {/* Existing list items with updated spacing */}
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-sm font-medium text-white shadow-sm">1</span>
                      <div>
                        <p className="font-medium text-gray-900">Board any Ajah-bound bus</p>
                        <p className="text-sm text-gray-500">From Oshodi, Yaba, or CMS. Ask for &ldquo;Lekki Phase 1 gate – Marwa&rdquo;</p>
                      </div>
                    </li>
                    {/* ... other list items with same pattern ... */}
                  </ol>
                  <p className="mt-3 text-xs text-gray-500">Total time: 3–5 minutes from Marwa</p>
                </div>
              </div>
            </details>

            <details className="group overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200 transition-all hover:ring-gray-300">
              <summary className="flex cursor-pointer items-center justify-between bg-gray-50 p-4 text-sm font-medium text-gray-900 hover:bg-gray-100">
                <div className="flex items-center gap-2">
                  <TruckIcon className="h-4 w-4 text-teal-600" />
                  <span>By Car</span>
                </div>
                <ChevronDownIcon className="h-5 w-5 text-gray-500 transition duration-200 group-open:rotate-180" />
              </summary>
              
              <div className="p-4">
                <ol className="space-y-4">
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-sm font-medium text-white shadow-sm">1</span>
                    <p className="text-gray-900">Stay in service lane past Lekki toll gate</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-sm font-medium text-white shadow-sm">2</span>
                    <p className="text-gray-900">At Marwa/2nd Roundabout, keep left and loop back</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-sm font-medium text-white shadow-sm">3</span>
                    <p className="text-gray-900">Take first U-turn, right into Providence St. We&apos;re 2nd building on right</p>
                  </li>
                </ol>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Contact & Location Footer */}
      <footer className="bg-gray-900" itemScope itemType="https://schema.org/MedicalBusiness">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {/* Address */}
            <address itemProp="address" className="flex items-start gap-3 text-gray-300 not-italic">
              <MapPinIcon className="h-6 w-6 shrink-0 text-teal-400" />
              <div>
                <p className="font-medium text-white">Our Location</p>
                <p className="mt-1.5 leading-relaxed">
                  40 Providence Street<br />
                  Lekki Phase 1, Lagos
                </p>
              </div>
            </address>

            {/* Opening hours */}
            <div itemProp="openingHours" content="Mo-Fr 08:00-17:00, Sa 09:00-15:00" className="flex items-start gap-3 text-gray-300">
              <svg className="h-6 w-6 shrink-0 text-teal-400" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <circle cx="12" cy="12" r="9" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3" />
              </svg>
              <div>
                <p className="font-medium text-white">Opening Hours</p>
                <p className="mt-1.5 leading-relaxed">
                  Mon – Fri: 8 am – 5 pm<br />
                  Saturday: 9 am – 3 pm
                </p>
              </div>
            </div>

            {/* Directions */}
            <div className="relative sm:text-right lg:col-start-3">
              <a
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700"
              >
                <span>Get Directions</span>
                <ArrowRightIcon className="h-4 w-4" />
              </a>
              
              {/* Map preview on hover - hidden on mobile */}
              <div className="absolute right-0 top-full z-10 mt-2 hidden rounded-lg shadow-xl group-hover:block md:block">
                <div className="relative h-48 w-64 overflow-hidden rounded-lg">
                  <Image
                    src={mapSrc}
                    alt="Map preview"
                    className="object-cover"
                    fill
                    sizes="256px"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <p className="text-center text-sm text-gray-400">
              © 2025 Vertex Diagnostic Center · 
              <a href="/privacy" className="mx-2 text-gray-300 hover:text-white">Privacy</a> · 
              <a href="/terms" className="text-gray-300 hover:text-white">Terms</a>
            </p>
          </div>
        </div>
      </footer>

      <footer className="site-footer site-footer-legal">
        <p className="legal-links">
          2025 Vertex · <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a>
        </p>
      </footer>

      {/* Scheduler Modal */}
      {showScheduler && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" aria-hidden="true" onClick={() => { setShowScheduler(false); setBookingStatus(null); }} />
            <div className="relative w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 sm:text-2xl">Choose Appointment</h3>
                {bookingStatus === "success" && (
                  <p className="mt-2 text-sm text-emerald-600">Appointment booked successfully!</p>
                )}
                {bookingStatus === "error" && (
                  <p className="mt-2 text-sm text-red-600">Please fill in all fields and try again.</p>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-900">
                    Date & Time
                  </label>
                  <div className="mt-1">
                    <DatePicker
                      id="date"
                      selected={selectedDateTime}
                      onChange={(date) => setSelectedDateTime(date)}
                      showTimeSelect
                      filterDate={(date) => date.getDay() !== 0}
                      filterTime={(time) => {
                        const day = time.getDay();
                        const hours = time.getHours();
                        const minutes = time.getMinutes();
                        if (day >= 1 && day <= 5) {
                          return (hours > 8 || (hours === 8 && minutes >= 0)) && (hours < 17 || (hours === 17 && minutes === 0));
                        }
                        if (day === 6) {
                          return (hours > 9 || (hours === 9 && minutes >= 0)) && (hours < 15 || (hours === 15 && minutes === 0));
                        }
                        return false;
                      }}
                      timeIntervals={30}
                      dateFormat="MMMM d, yyyy h:mm aa"
                      className="block w-full rounded-lg border-gray-300 py-2.5 pl-3 pr-10 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      placeholderText="Select date & time"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                    Your Email
                  </label>
                  <div className="mt-1">
                    <input
                      type="email"
                      id="email"
                      value={selectedEmail}
                      onChange={(e) => setSelectedEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="block w-full rounded-lg border-gray-300 py-2.5 pl-3 pr-10 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowScheduler(false); setBookingStatus(null); }}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!selectedDateTime || !selectedEmail) {
                      setBookingStatus("error");
                      return;
                    }
                    try {
                      const res = await fetch("/api/book-appointment", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ datetime: selectedDateTime, email: selectedEmail }),
                      });
                      if (res.ok) setBookingStatus("success");
                      else setBookingStatus("error");
                    } catch {
                      setBookingStatus("error");
                    }
                  }}
                  className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                >
                  Confirm Appointment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
